const escapeHtml = value => String(value || "")
  .replaceAll("&", "&amp;")
  .replaceAll("<", "&lt;")
  .replaceAll(">", "&gt;")
  .replaceAll('"', "&quot;")
  .replaceAll("'", "&#39;")

const normalize = value => String(value || "")
  .toLocaleLowerCase("es")
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")

const decodeEmail = value => {
  try {
    return window.atob(String(value || "")).split("").reverse().join("")
  } catch (_error) {
    return ""
  }
}

const copyEmail = async email => {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(email)
    return
  }

  const input = document.createElement("textarea")
  input.value = email
  input.setAttribute("readonly", "")
  input.style.position = "fixed"
  input.style.opacity = "0"
  document.body.append(input)
  input.select()
  document.execCommand("copy")
  input.remove()
}

const debounce = (fn, wait = 200) => {
  let timeout
  return (...args) => {
    window.clearTimeout(timeout)
    timeout = window.setTimeout(() => fn(...args), wait)
  }
}

document.addEventListener("DOMContentLoaded", async () => {
  const app = document.getElementById("cartografia-app")
  if (!app) return

  const form = document.getElementById("cartografia-filters")
  const themeFilters = document.getElementById("carto-theme-filters")
  const resultsCount = document.getElementById("carto-results-count")
  const results = document.getElementById("carto-results")
  const empty = document.getElementById("carto-results-empty")
  const clearBtn = document.getElementById("carto-clear-filters")
  const activeFilters = document.getElementById("carto-active-filters")
  const loadMoreBtn = document.getElementById("carto-load-more")
  const viewToggles = Array.from(document.querySelectorAll(".carto-view-toggle"))

  if (!form || !themeFilters || !resultsCount || !results || !empty || !clearBtn || !activeFilters || !loadMoreBtn) return

  const qInput = document.getElementById("carto-search")
  const typeInput = document.getElementById("carto-type")
  const sectorInput = document.getElementById("carto-sector")
  const institutionInput = document.getElementById("carto-institution")
  const sortInput = document.getElementById("carto-sort")

  const endpoint = app.dataset.endpoint || "/cartografia-data.json"

  let entities = []
  let themes = []
  let institutions = []

  try {
    const response = await fetch(endpoint, { headers: { Accept: "application/json" } })
    if (!response.ok) throw new Error(`HTTP ${response.status}`)

    const payload = await response.json()
    entities = Array.isArray(payload.entities) ? payload.entities : []
    themes = Array.isArray(payload.themes) ? payload.themes : []
    institutions = Array.isArray(payload.institutions) ? payload.institutions : []
  } catch (_error) {
    resultsCount.textContent = "No se pudieron cargar los datos en este momento."
    return
  }

  const state = {
    q: "",
    type: "",
    sector: "",
    institution: "",
    sort: "relevance",
    view: "cards",
    themes: new Set(),
    visibleCount: 24
  }

  const fillInstitutionOptions = () => {
    const options = ["<option value=\"\">Todas</option>"]
    institutions.forEach(institution => {
      options.push(`<option value="${escapeHtml(institution)}">${escapeHtml(institution)}</option>`)
    })
    institutionInput.innerHTML = options.join("")
  }

  const renderThemeButtons = () => {
    themeFilters.innerHTML = themes
      .map(theme => {
        const selected = state.themes.has(theme)
        return `<button type="button" data-theme="${escapeHtml(theme)}" class="rounded-full border px-3 py-1 text-sm/6 transition-colors ${selected ? "border-[#4A8E85] bg-[#4A8E85]/12 text-[#2F6760]" : "border-olive-300 bg-white text-olive-700 hover:border-[#4A8E85]/40"}">${escapeHtml(theme)}</button>`
      })
      .join("")
  }

  const syncControlsFromState = () => {
    qInput.value = state.q
    typeInput.value = state.type
    sectorInput.value = state.sector
    institutionInput.value = state.institution
    sortInput.value = state.sort
    updateViewToggles()
    renderThemeButtons()
  }

  const syncStateFromControls = () => {
    state.q = qInput.value.trim()
    state.type = typeInput.value
    state.sector = sectorInput.value
    state.institution = institutionInput.value
    state.sort = sortInput.value
    state.visibleCount = 24
  }

  const readStateFromUrl = () => {
    const params = new URLSearchParams(window.location.search)
    state.q = params.get("q") || ""
    state.type = params.get("type") || ""
    state.sector = params.get("sector") || ""
    state.institution = params.get("institution") || ""
    state.sort = params.get("sort") || "relevance"
    state.view = params.get("view") || "cards"
    state.visibleCount = Number(params.get("n") || 24)

    if (!Number.isFinite(state.visibleCount) || state.visibleCount < 24) {
      state.visibleCount = 24
    }

    state.themes = new Set(params.getAll("theme").filter(Boolean))
  }

  const writeStateToUrl = () => {
    const params = new URLSearchParams()

    if (state.q) params.set("q", state.q)
    if (state.type) params.set("type", state.type)
    if (state.sector) params.set("sector", state.sector)
    if (state.institution) params.set("institution", state.institution)
    if (state.sort && state.sort !== "relevance") params.set("sort", state.sort)
    if (state.view && state.view !== "cards") params.set("view", state.view)
    if (state.visibleCount > 24) params.set("n", String(state.visibleCount))
    Array.from(state.themes).sort().forEach(theme => params.append("theme", theme))

    const search = params.toString()
    const nextUrl = `${window.location.pathname}${search ? `?${search}` : ""}`
    window.history.replaceState({}, "", nextUrl)
  }

  const relevanceScore = entity => {
    if (!state.q) return 0

    const q = normalize(state.q)
    const haystacks = [
      entity.name,
      entity.entity_name,
      entity.institution_display,
      entity.research_group,
      entity.details,
      ...(entity.thematic_areas || [])
    ]

    let score = 0
    haystacks.forEach(value => {
      const text = normalize(value)
      if (!text) return
      if (text === q) score += 5
      else if (text.startsWith(q)) score += 3
      else if (text.includes(q)) score += 1
    })

    return score
  }

  const filterEntities = () => {
    const q = normalize(state.q)

    return entities.filter(entity => {
      if (state.type && entity.inscription !== state.type) return false
      if (state.sector && entity.public_private !== state.sector) return false
      if (state.institution && entity.institution_display !== state.institution) return false

      if (state.themes.size > 0) {
        const themesInEntity = new Set(entity.thematic_areas || [])
        for (const theme of state.themes) {
          if (!themesInEntity.has(theme)) return false
        }
      }

      if (q) {
        const fields = [
          entity.name,
          entity.entity_name,
          entity.institution_display,
          entity.research_group,
          entity.details,
          ...(entity.thematic_areas || [])
        ]

        const matches = fields.some(value => normalize(value).includes(q))
        if (!matches) return false
      }

      return true
    })
  }

  const sortEntities = list => {
    const sorted = [...list]

    if (state.sort === "name") {
      sorted.sort((a, b) => a.name.localeCompare(b.name, "es"))
      return sorted
    }

    if (state.sort === "themes") {
      sorted.sort((a, b) => {
        const diff = (b.thematic_areas || []).length - (a.thematic_areas || []).length
        if (diff !== 0) return diff
        return a.name.localeCompare(b.name, "es")
      })
      return sorted
    }

    if (state.sort === "type") {
      const order = { Individual: 0, Institutional: 1 }
      sorted.sort((a, b) => {
        const diff = (order[a.inscription] ?? 9) - (order[b.inscription] ?? 9)
        if (diff !== 0) return diff
        return a.name.localeCompare(b.name, "es")
      })
      return sorted
    }

    sorted.sort((a, b) => {
      const scoreDiff = relevanceScore(b) - relevanceScore(a)
      if (scoreDiff !== 0) return scoreDiff
      return a.name.localeCompare(b.name, "es")
    })

    return sorted
  }

  const typeLabel = value => value === "Institutional" ? "Institucional" : "Individual"

  const revealedEmailHtml = email => `
    <span class="inline-flex items-center gap-1.5">
      <a href="mailto:${escapeHtml(email)}" class="font-medium text-[#356F68] underline underline-offset-4">${escapeHtml(email)}</a>
      <button type="button" data-copy-email="${escapeHtml(email)}" class="inline-flex size-7 items-center justify-center rounded-md text-[#356F68] hover:bg-[#4A8E85]/10 focus:outline-none focus:ring-2 focus:ring-[#4A8E85]/30" aria-label="Copiar email" title="Copiar email">
        <svg aria-hidden="true" viewBox="0 0 24 24" class="size-4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <rect width="14" height="14" x="8" y="8" rx="2"></rect>
          <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"></path>
        </svg>
      </button>
      <span data-copy-status class="hidden rounded-md bg-[#4A8E85]/10 px-2 py-0.5 text-xs/5 font-medium text-[#356F68]">Email copiado</span>
    </span>
  `

  const renderCards = list => {
    results.innerHTML = list.map(entity => {
      const chips = (entity.thematic_areas || []).map(theme => `<span class="rounded-full bg-[#4A8E85]/10 px-2.5 py-1 text-xs/5 font-medium text-[#356F68]">${escapeHtml(theme)}</span>`).join("")
      const hasExtra = Boolean(entity.details || entity.name_platforms)
      const emailButton = entity.email_encoded ? `<button type="button" data-email="${escapeHtml(entity.email_encoded)}" class="mt-1 text-sm/6 font-medium text-[#356F68] underline underline-offset-4">Mostrar email</button>` : ""

      return `
        <article class="rounded-xl bg-white p-4 ring-1 ring-black/8">
          <div class="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 class="text-base/7 font-medium text-olive-950">${escapeHtml(entity.name)}</h3>
              <p class="text-sm/6 text-olive-700">${escapeHtml(entity.entity_name || entity.institution_display || "Sin entidad")}</p>
              ${emailButton ? `<p>${emailButton}</p>` : ""}
            </div>
            <div class="flex items-center gap-2">
              <span class="rounded-full bg-olive-200/60 px-2.5 py-1 text-xs/5 font-medium text-olive-700">${escapeHtml(typeLabel(entity.inscription))}</span>
              ${entity.public_private ? `<span class="rounded-full bg-olive-200/60 px-2.5 py-1 text-xs/5 font-medium text-olive-700">${escapeHtml(entity.public_private)}</span>` : ""}
            </div>
          </div>

          <div class="mt-3 flex flex-wrap gap-2">${chips}</div>

          ${hasExtra ? `
            <details class="mt-3 rounded-lg bg-olive-100/70 p-3">
              <summary class="cursor-pointer text-sm/6 font-medium text-[#356F68]">Más detalle</summary>
              ${entity.details ? `<p class="mt-2 text-sm/7 text-olive-700">${escapeHtml(entity.details)}</p>` : ""}
              ${entity.name_platforms ? `<p class="mt-2 text-sm/7 text-olive-700"><span class="font-medium text-olive-900">Otras plataformas:</span> ${escapeHtml(entity.name_platforms)}</p>` : ""}
            </details>
          ` : ""}
        </article>
      `
    }).join("")
  }

  const renderTable = list => {
    const rows = list.map(entity => `
      <tr class="border-b border-black/8 align-top last:border-b-0">
        <td class="px-3 py-2 text-sm/6 font-medium text-olive-950">${escapeHtml(entity.name)}</td>
        <td class="px-3 py-2 text-sm/6 text-olive-700">${escapeHtml(typeLabel(entity.inscription))}</td>
        <td class="px-3 py-2 text-sm/6 text-olive-700">${escapeHtml(entity.entity_name || entity.institution_display || "-")}</td>
        <td class="px-3 py-2 text-sm/6 text-olive-700">${entity.email_encoded ? `<button type="button" data-email="${escapeHtml(entity.email_encoded)}" class="font-medium text-[#356F68] underline underline-offset-4">Mostrar email</button>` : "-"}</td>
        <td class="px-3 py-2 text-sm/6 text-olive-700">${escapeHtml((entity.thematic_areas || []).join(", "))}</td>
        <td class="px-3 py-2 text-sm/6 text-olive-700">${escapeHtml(entity.details || "-")}</td>
      </tr>
    `).join("")

    results.innerHTML = `
      <div class="overflow-x-auto rounded-xl bg-white ring-1 ring-black/8">
        <table class="min-w-full border-collapse">
          <thead>
            <tr class="border-b border-black/10 bg-olive-100/70 text-left">
              <th scope="col" class="px-3 py-2 text-xs/5 font-semibold uppercase tracking-wide text-olive-700">Nombre</th>
              <th scope="col" class="px-3 py-2 text-xs/5 font-semibold uppercase tracking-wide text-olive-700">Tipo</th>
              <th scope="col" class="px-3 py-2 text-xs/5 font-semibold uppercase tracking-wide text-olive-700">Entidad</th>
              <th scope="col" class="px-3 py-2 text-xs/5 font-semibold uppercase tracking-wide text-olive-700">Email</th>
              <th scope="col" class="px-3 py-2 text-xs/5 font-semibold uppercase tracking-wide text-olive-700">Áreas</th>
              <th scope="col" class="px-3 py-2 text-xs/5 font-semibold uppercase tracking-wide text-olive-700">Motivación</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
    `
  }

  const renderActiveFilters = () => {
    const chips = []

    if (state.q) chips.push({ key: "q", label: `Buscar: ${state.q}` })
    if (state.type) chips.push({ key: "type", label: `Tipo: ${typeLabel(state.type)}` })
    if (state.sector) chips.push({ key: "sector", label: `Sector: ${state.sector}` })
    if (state.institution) chips.push({ key: "institution", label: `Institución: ${state.institution}` })
    Array.from(state.themes).sort().forEach(theme => chips.push({ key: `theme:${theme}`, label: `Área: ${theme}` }))

    activeFilters.innerHTML = chips
      .map(chip => `<button type="button" data-remove-filter="${escapeHtml(chip.key)}" class="rounded-full bg-[#4A8E85]/10 px-2.5 py-1 text-xs/5 font-medium text-[#356F68] hover:bg-[#4A8E85]/18">${escapeHtml(chip.label)} ×</button>`)
      .join("")
  }

  const updateViewToggles = () => {
    viewToggles.forEach(button => {
      const active = button.dataset.view === state.view
      button.setAttribute("aria-pressed", active ? "true" : "false")
      button.classList.toggle("bg-white", active)
      button.classList.toggle("text-olive-950", active)
      button.classList.toggle("shadow-sm", active)
      button.classList.toggle("text-olive-700", !active)
    })
  }

  const updateLoadMore = (total, shown) => {
    const hasMore = shown < total
    loadMoreBtn.classList.toggle("hidden", !hasMore)
    loadMoreBtn.textContent = hasMore ? `Mostrar más resultados (${total - shown} restantes)` : "Mostrar más resultados"
  }

  const render = () => {
    const filtered = sortEntities(filterEntities())
    const shown = filtered.slice(0, state.visibleCount)

    resultsCount.textContent = `${filtered.length} resultados de ${entities.length}`

    empty.classList.toggle("hidden", filtered.length !== 0)
    results.classList.toggle("hidden", filtered.length === 0)

    if (filtered.length > 0) {
      if (state.view === "table") renderTable(shown)
      else renderCards(shown)
    }

    updateLoadMore(filtered.length, shown.length)
    renderThemeButtons()
    renderActiveFilters()
    updateViewToggles()
    writeStateToUrl()
  }

  const onChange = () => {
    syncStateFromControls()
    render()
  }

  const resetFilters = () => {
    state.q = ""
    state.type = ""
    state.sector = ""
    state.institution = ""
    state.sort = "relevance"
    state.themes = new Set()
    state.visibleCount = 24
    syncControlsFromState()
    render()
  }

  fillInstitutionOptions()
  readStateFromUrl()
  syncControlsFromState()
  render()

  form.addEventListener("change", onChange)
  qInput.addEventListener("input", debounce(onChange, 200))

  themeFilters.addEventListener("click", event => {
    const button = event.target.closest("button[data-theme]")
    if (!button) return

    const theme = button.dataset.theme
    if (!theme) return

    if (state.themes.has(theme)) state.themes.delete(theme)
    else state.themes.add(theme)

    state.visibleCount = 24
    render()
  })

  viewToggles.forEach(button => {
    button.addEventListener("click", () => {
      state.view = button.dataset.view === "table" ? "table" : "cards"
      render()
    })
  })

  clearBtn.addEventListener("click", resetFilters)

  loadMoreBtn.addEventListener("click", () => {
    state.visibleCount += 24
    render()
  })

  activeFilters.addEventListener("click", event => {
    const button = event.target.closest("button[data-remove-filter]")
    if (!button) return

    const key = button.dataset.removeFilter
    if (!key) return

    if (key === "q") state.q = ""
    else if (key === "type") state.type = ""
    else if (key === "sector") state.sector = ""
    else if (key === "institution") state.institution = ""
    else if (key.startsWith("theme:")) state.themes.delete(key.replace("theme:", ""))

    state.visibleCount = 24
    syncControlsFromState()
    render()
  })

  results.addEventListener("click", event => {
    const copyButton = event.target.closest("button[data-copy-email]")
    if (copyButton) {
      const email = copyButton.dataset.copyEmail
      if (!email) return

      copyEmail(email).then(() => {
        const status = copyButton.parentElement?.querySelector("[data-copy-status]")
        copyButton.setAttribute("aria-label", "Email copiado")
        copyButton.setAttribute("title", "Email copiado")
        status?.classList.remove("hidden")
        window.setTimeout(() => {
          copyButton.setAttribute("aria-label", "Copiar email")
          copyButton.setAttribute("title", "Copiar email")
          status?.classList.add("hidden")
        }, 1600)
      }).catch(() => {
        const status = copyButton.parentElement?.querySelector("[data-copy-status]")
        copyButton.setAttribute("aria-label", "No se pudo copiar")
        copyButton.setAttribute("title", "No se pudo copiar")
        if (status) {
          status.textContent = "No se pudo copiar"
          status.classList.remove("hidden")
        }
      })
      return
    }

    const button = event.target.closest("button[data-email]")
    if (!button) return

    const email = decodeEmail(button.dataset.email)
    if (!email) return

    const wrapper = document.createElement("span")
    wrapper.innerHTML = revealedEmailHtml(email)
    button.replaceWith(wrapper.firstElementChild)
  })
})
